import { Check, Globe } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { useTranslation } from "react-i18next"

import { getLangMeta, SUPPORTED_LANGS, type SupportedLang } from "@/shared/i18n"
import { cn } from "@/shared/utils"

type LanguageSwitcherProps = {
  variant?: "default" | "ghost" | "dark"
  align?: "left" | "right"
  className?: string
}

type DocumentWithViewTransition = Document & {
  startViewTransition?: (callback: () => void) => {
    ready: Promise<void>
    finished: Promise<void>
  }
}

const TRANSITION_DURATION = 400

function runShrinkGrow() {
  document.documentElement.animate(
    [
      { transform: "scale(0.9)", opacity: 0 },
      { transform: "scale(1)", opacity: 1 },
    ],
    {
      duration: TRANSITION_DURATION * 1.2,
      easing: "cubic-bezier(0.19, 1, 0.22, 1)",
      pseudoElement: "::view-transition-new(root)",
    },
  )
  document.documentElement.animate(
    [
      { transform: "scale(1)", opacity: 1 },
      { transform: "scale(1.05)", opacity: 0 },
    ],
    {
      duration: TRANSITION_DURATION * 1.2,
      easing: "cubic-bezier(0.19, 1, 0.22, 1)",
      pseudoElement: "::view-transition-old(root)",
    },
  )
}

const FOCUS_RESET =
  "focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0"

export function LanguageSwitcher({
  variant = "default",
  align = "left",
  className,
}: LanguageSwitcherProps) {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const current = (i18n.resolvedLanguage ?? i18n.language ?? "pt-BR") as SupportedLang
  const currentMeta = getLangMeta(current)

  useEffect(() => {
    if (!open) {return}
    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {setOpen(false)}
    }
    document.addEventListener("mousedown", handleClick)
    document.addEventListener("keydown", handleEsc)
    return () => {
      document.removeEventListener("mousedown", handleClick)
      document.removeEventListener("keydown", handleEsc)
    }
  }, [open])

  const change = (lang: SupportedLang) => {
    setOpen(false)
    if (lang === current) {return}

    const applyLang = () => {
      flushSync(() => {
        void i18n.changeLanguage(lang)
        document.documentElement.lang = lang
      })
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches

    const doc = document as DocumentWithViewTransition
    if (prefersReducedMotion || !doc.startViewTransition) {
      applyLang()
      return
    }

    const transition = doc.startViewTransition(applyLang)
    transition.ready
      .then(() => {
        runShrinkGrow()
      })
      .catch(() => {
        
      })
  }

  const isDark = variant === "dark"
  const triggerBase =
    "inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-full border px-3 font-mono text-[10.5px] font-semibold uppercase tracking-[0.16em] transition-colors duration-200"
  const triggerLight =
    "border-mesa-border bg-mesa-bg-subtle text-mesa-text-muted hover:border-mesa-primary/20 hover:text-mesa-primary"
  const triggerDark =
    "border-white/10 bg-white/5 text-white/70 hover:border-white/20 hover:text-white"
  const triggerGhost =
    "border-transparent bg-transparent text-mesa-text-muted hover:text-mesa-primary"

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn(
          triggerBase,
          FOCUS_RESET,
          variant === "default" && triggerLight,
          variant === "dark" && triggerDark,
          variant === "ghost" && triggerGhost,
        )}
      >
        <Globe className="h-3.5 w-3.5" aria-hidden="true" strokeWidth={1.75} />
        <span>{currentMeta.short}</span>
      </button>

      {open ? (
        <ul
          role="listbox"
          aria-label="Selecionar idioma"
          className={cn(
            "absolute bottom-full z-50 mb-2 min-w-[200px] overflow-hidden rounded-xl border shadow-lg",
            align === "right" ? "right-0" : "left-0",
            isDark
              ? "border-white/10 bg-[#0A0E1A]"
              : "border-mesa-border bg-card",
          )}
        >
          {SUPPORTED_LANGS.map((lang) => {
            const meta = getLangMeta(lang)
            const active = lang === current
            return (
              <li key={lang}>
                <button
                  type="button"
                  onClick={() => change(lang)}
                  role="option"
                  aria-selected={active}
                  className={cn(
                    "flex w-full cursor-pointer items-center justify-between gap-3 px-3.5 py-2.5 text-left text-[13px] transition-colors duration-150",
                    FOCUS_RESET,
                    isDark
                      ? active
                        ? "bg-white/10 text-white"
                        : "text-white/75 hover:bg-white/5 hover:text-white"
                      : active
                        ? "bg-mesa-bg-subtle text-mesa-primary"
                        : "text-mesa-text hover:bg-mesa-bg-subtle hover:text-mesa-primary",
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] opacity-60">
                      {meta.short}
                    </span>
                    <span className="font-medium">{meta.native}</span>
                  </span>
                  {active ? (
                    <Check className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
                  ) : null}
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
